/**
 * Map Export / Import — full snapshot mapy tematycznej do JSON + re-import.
 *
 * Use case: przerzut mapy sushi (albo dowolnej innej) między środowiskami
 * / kontami / DB branchami bez SQL dumpów. User pobiera JSON, uploaduje pod
 * inną nazwą → dostaje identyczną kopię z nowymi UUID-ami (idempotent).
 *
 * Export zawiera PEŁNĄ MAPĘ + CONTENT PIPELINE:
 *   - thematic_maps (macro/SBD/audit_report/repair_report/paa baseline)
 *   - thematic_seeds (z status + seed_type + scope_coverage)
 *   - thematic_nodes (z paa_preview + matched_wp_post_id)
 *   - thematic_entities (+ entity_attributes EAV)
 *   - thematic_node_relations
 *   - thematic_writing_profiles
 *   - thematic_node_content (outline, draft, final HTML, engine_stage — pełny pipeline)
 *   - content_knowledge_graphs (KG per node — 3-call NER + data + assembly output)
 *   - content_serp_cache (SERP top 10 URLs — można pominąć, regeneruje się w CE)
 *
 * Export NIE zawiera (transient / per-instancja / globalne):
 *   - generation_queue / publish_queue (runtime state)
 *   - map_repair_log (audit trail konkretnej DB, zbyt dużo kontekstu)
 *   - thematic_prompts (globalne, nie per-map)
 *   - wp_posts (per-website — tylko matched ID zachowujemy gdy preserveWpMatches
 *     + nowa mapa ma ten sam website_id; nieistniejące ID są clearowane)
 */

import { supabase } from "@/integrations/supabase/client";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export const MAP_EXPORT_VERSION = "v1" as const;

// ── Types ───────────────────────────────────────────────────────────────

export interface MapExportPayload {
  export_version: typeof MAP_EXPORT_VERSION;
  exported_at: string;
  source_map_id: string;
  source_map_name: string;
  source_website_id: string | null;
  counts: {
    seeds: number;
    nodes: number;
    entities: number;
    entity_attributes: number;
    relations: number;
    writing_profiles: number;
    node_content: number;
    knowledge_graphs: number;
    serp_cache: number;
  };
  map: Record<string, unknown>;
  seeds: Record<string, unknown>[];
  nodes: Record<string, unknown>[];
  entities: Record<string, unknown>[];
  entity_attributes: Record<string, unknown>[];
  relations: Record<string, unknown>[];
  writing_profiles: Record<string, unknown>[];
  // Content pipeline — opcjonalne (v1 bez tych pól → import je pomija)
  node_content?: Record<string, unknown>[];
  knowledge_graphs?: Record<string, unknown>[];
  serp_cache?: Record<string, unknown>[];
}

export interface ImportOptions {
  newName: string;
  websiteId?: string | null; // override source website (domyślnie: zachowaj source)
  preserveWpMatches?: boolean; // zachowaj matched_wp_post_id (wymaga istnienia tych postów)
  status?: "draft" | "active" | "archived"; // domyślnie "draft"
}

export interface ImportResult {
  success: true;
  newMapId: string;
  newName: string;
  counts: {
    seeds: number;
    nodes: number;
    entities: number;
    entity_attributes: number;
    relations: number;
    writing_profiles: number;
    node_content: number;
    knowledge_graphs: number;
    serp_cache: number;
    wp_matches_preserved: number;
    wp_matches_cleared: number;
  };
}

// ── Export ──────────────────────────────────────────────────────────────

/**
 * Cheap COUNT(*) dla wszystkich 9 sekcji — uzywane w UI przed ExportOptionsDialog,
 * zeby user widzial liczby zanim zdecyduje co eksportowac.
 */
export async function countExportSections(mapId: string): Promise<{
  seeds: number; nodes: number; entities: number; entity_attributes: number;
  relations: number; writing_profiles: number; node_content: number;
  knowledge_graphs: number; serp_cache: number;
}> {
  // Najpierw nodes (do JOIN dla KG/SERP)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: nodesData } = await (db as any)
    .from("thematic_nodes")
    .select("id")
    .eq("map_id", mapId);
  const nodeIds = ((nodesData ?? []) as { id: string }[]).map((n) => n.id);

  const countOne = async (table: string, filter: "map_id" | "node_id") => {
    const col = filter === "map_id" ? "map_id" : "node_id";
    if (col === "node_id" && nodeIds.length === 0) return 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const q = (db as any).from(table).select("*", { count: "exact", head: true });
    const r = col === "map_id" ? await q.eq("map_id", mapId) : await q.in("node_id", nodeIds);
    return Number(r.count ?? 0);
  };

  const [
    seeds, entities, entity_attributes, relations, writing_profiles,
    node_content, knowledge_graphs, serp_cache,
  ] = await Promise.all([
    countOne("thematic_seeds", "map_id"),
    countOne("thematic_entities", "map_id"),
    countOne("thematic_entity_attributes", "map_id"),
    countOne("thematic_node_relations", "map_id"),
    countOne("thematic_writing_profiles", "map_id"),
    countOne("thematic_node_content", "map_id"),
    countOne("content_knowledge_graphs", "node_id"),
    countOne("content_serp_cache", "node_id"),
  ]);

  return {
    seeds, nodes: nodeIds.length, entities, entity_attributes, relations,
    writing_profiles, node_content, knowledge_graphs, serp_cache,
  };
}

/**
 * Opcje selektywne dla debugowania — user wybiera jakie sekcje zapisac.
 * Wszystko domyslnie ON (backward compat: stare wywolania bez opts = pelny eksport).
 *
 * Zawsze w eksporcie: macro (thematic_maps row). Reszta opcjonalna.
 *
 * Dependencies (auto-off gdy parent off):
 *   - includeEav wymaga includeEntities
 *   - includeRelations / includeNodeContent / includeKnowledgeGraphs / includeSerpCache
 *     wymagaja includeNodes (bo maja FK na node_id)
 */
export interface ExportOptions {
  includeSeeds?: boolean;          // thematic_seeds
  includeNodes?: boolean;          // thematic_nodes (+cascade: relations/CE/KG/SERP)
  includeEntities?: boolean;       // thematic_entities
  includeEav?: boolean;            // thematic_entity_attributes (wymaga entities)
  includeRelations?: boolean;      // thematic_node_relations (wymaga nodes)
  includeWritingProfiles?: boolean;// thematic_writing_profiles
  includeNodeContent?: boolean;    // thematic_node_content (wymaga nodes)
  includeKnowledgeGraphs?: boolean;// content_knowledge_graphs (wymaga nodes)
  includeSerpCache?: boolean;      // content_serp_cache (wymaga nodes)
}

const DEFAULT_EXPORT_OPTIONS: Required<ExportOptions> = {
  includeSeeds: true,
  includeNodes: true,
  includeEntities: true,
  includeEav: true,
  includeRelations: true,
  includeWritingProfiles: true,
  includeNodeContent: true,
  includeKnowledgeGraphs: true,
  includeSerpCache: true,
};

export async function exportMapToJson(
  mapId: string,
  options: ExportOptions = {},
): Promise<MapExportPayload> {
  const opts = { ...DEFAULT_EXPORT_OPTIONS, ...options };

  // Enforce dependencies — jesli user wylaczy nody, zalezne sekcje musza paść
  // bez wzgledu na ich wlasny checkbox (FK integrity).
  if (!opts.includeNodes) {
    opts.includeRelations = false;
    opts.includeNodeContent = false;
    opts.includeKnowledgeGraphs = false;
    opts.includeSerpCache = false;
  }
  if (!opts.includeEntities) {
    opts.includeEav = false;
  }

  // Map row (ZAWSZE — to jest sama definicja mapy / macro)
  const { data: map, error: mapErr } = await db
    .from("thematic_maps")
    .select("*")
    .eq("id", mapId)
    .maybeSingle();
  if (mapErr) throw mapErr;
  if (!map) throw new Error(`Mapa ${mapId} nie istnieje`);

  // Seeds + Nodes — opcjonalne ale default ON
  const coreQueries: Array<Promise<unknown>> = [];
  const coreKeys: string[] = [];
  if (opts.includeSeeds) {
    coreQueries.push(db.from("thematic_seeds").select("*").eq("map_id", mapId));
    coreKeys.push("seeds");
  }
  if (opts.includeNodes) {
    coreQueries.push(db.from("thematic_nodes").select("*").eq("map_id", mapId));
    coreKeys.push("nodes");
  }

  let seeds: Record<string, unknown>[] = [];
  let nodes: Record<string, unknown>[] = [];
  if (coreQueries.length > 0) {
    const coreResults = await Promise.all(coreQueries);
    coreResults.forEach((res, i) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const r = res as { data: unknown[] | null; error: any };
      if (r.error) throw r.error;
      const bucket = (r.data || []) as Record<string, unknown>[];
      if (coreKeys[i] === "seeds") seeds = bucket;
      else if (coreKeys[i] === "nodes") nodes = bucket;
    });
  }

  // Opcjonalne sekcje — fetch tylko gdy opts pozwala (oszczedza SQL roundtripy)
  const optionalQueries: Array<Promise<unknown>> = [];
  const optionalKeys: string[] = [];

  if (opts.includeRelations) {
    optionalQueries.push(db.from("thematic_node_relations").select("*").eq("map_id", mapId));
    optionalKeys.push("relations");
  }
  if (opts.includeEntities) {
    optionalQueries.push(db.from("thematic_entities").select("*").eq("map_id", mapId));
    optionalKeys.push("entities");
  }
  if (opts.includeEav) {
    optionalQueries.push(db.from("thematic_entity_attributes").select("*").eq("map_id", mapId));
    optionalKeys.push("entity_attributes");
  }
  if (opts.includeWritingProfiles) {
    optionalQueries.push(db.from("thematic_writing_profiles").select("*").eq("map_id", mapId));
    optionalKeys.push("writing_profiles");
  }

  const optionalResults = await Promise.all(optionalQueries);
  const optionalData: Record<string, Record<string, unknown>[]> = {
    relations: [],
    entities: [],
    entity_attributes: [],
    writing_profiles: [],
  };
  optionalResults.forEach((res, i) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const r = res as { data: unknown[] | null; error: any };
    if (r.error) throw r.error;
    optionalData[optionalKeys[i]] = (r.data || []) as Record<string, unknown>[];
  });

  const relations = optionalData.relations;
  const entities = optionalData.entities;
  const entity_attributes = optionalData.entity_attributes;
  const writing_profiles = optionalData.writing_profiles;

  // Content pipeline — per-node tabele (JOIN via node_id)
  const nodeIds = nodes.map((n) => n.id as string);
  let node_content: Record<string, unknown>[] = [];
  let knowledge_graphs: Record<string, unknown>[] = [];
  let serp_cache: Record<string, unknown>[] = [];

  const contentQueries: Array<Promise<unknown>> = [];
  const contentKeys: string[] = [];

  if (nodeIds.length > 0) {
    if (opts.includeNodeContent) {
      contentQueries.push(db.from("thematic_node_content").select("*").eq("map_id", mapId));
      contentKeys.push("node_content");
    }
    if (opts.includeKnowledgeGraphs) {
      contentQueries.push(db.from("content_knowledge_graphs").select("*").in("node_id", nodeIds));
      contentKeys.push("knowledge_graphs");
    }
    if (opts.includeSerpCache) {
      contentQueries.push(db.from("content_serp_cache").select("*").in("node_id", nodeIds));
      contentKeys.push("serp_cache");
    }
  }

  if (contentQueries.length > 0) {
    const contentResults = await Promise.all(contentQueries);
    contentResults.forEach((res, i) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const r = res as { data: unknown[] | null; error: any };
      if (r.error) throw r.error;
      const bucket = (r.data || []) as Record<string, unknown>[];
      if (contentKeys[i] === "node_content") node_content = bucket;
      else if (contentKeys[i] === "knowledge_graphs") knowledge_graphs = bucket;
      else if (contentKeys[i] === "serp_cache") serp_cache = bucket;
    });
  }

  return {
    export_version: MAP_EXPORT_VERSION,
    exported_at: new Date().toISOString(),
    source_map_id: map.id,
    source_map_name: map.name,
    source_website_id: map.website_id ?? null,
    counts: {
      seeds: seeds.length,
      nodes: nodes.length,
      entities: entities.length,
      entity_attributes: entity_attributes.length,
      relations: relations.length,
      writing_profiles: writing_profiles.length,
      node_content: node_content.length,
      knowledge_graphs: knowledge_graphs.length,
      serp_cache: serp_cache.length,
    },
    map: map as Record<string, unknown>,
    seeds,
    nodes,
    entities,
    entity_attributes,
    relations,
    writing_profiles,
    node_content,
    knowledge_graphs,
    serp_cache,
  };
}

export function downloadMapExport(
  payload: MapExportPayload,
  customFilename?: string,
): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  const safeName = payload.source_map_name
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 40);
  const filename = customFilename || `map-${safeName}-${stamp}.json`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Import ──────────────────────────────────────────────────────────────

/**
 * Walidacja payload'u. Zwraca błąd jeśli nie rozpoznajemy formatu.
 */
export function validateMapExportPayload(
  raw: unknown,
): asserts raw is MapExportPayload {
  if (!raw || typeof raw !== "object") {
    throw new Error("Plik nie zawiera poprawnego JSON mapy");
  }
  const p = raw as Record<string, unknown>;
  if (p.export_version !== MAP_EXPORT_VERSION) {
    throw new Error(
      `Nieobsługiwana wersja export_version="${p.export_version}" (oczekiwane: ${MAP_EXPORT_VERSION})`,
    );
  }
  const required = [
    "map",
    "seeds",
    "nodes",
    "entities",
    "entity_attributes",
    "relations",
    "writing_profiles",
  ];
  for (const key of required) {
    if (!(key in p)) {
      throw new Error(`Plik uszkodzony — brak pola "${key}"`);
    }
  }
  if (typeof (p.map as Record<string, unknown>).name !== "string") {
    throw new Error("Plik uszkodzony — brak map.name");
  }
}

/**
 * Import mapy z payload'u.
 *
 * Strategia:
 *   1. Pobierz current user (wszystkie INSERTy na jego user_id)
 *   2. INSERT thematic_maps (new UUID, nowa nazwa, status='draft')
 *   3. Build oldId→newId maps dla seeds, entities, nodes (klucz: old UUID, wartość: new UUID generowane po stronie DB via gen_random_uuid())
 *   4. INSERT seeds → mapuj seed_id
 *   5. INSERT entities → mapuj entity_id
 *   6. INSERT nodes (seed_id + primary_entity_id remap) → mapuj node_id
 *   7. INSERT entity_attributes (entity_id remap)
 *   8. INSERT relations (from_node_id + to_node_id + map_id remap)
 *   9. INSERT writing_profiles (map_id remap) → ewentualnie mapuj active_profile_id
 *
 * matched_wp_post_id na nodach:
 *   - preserveWpMatches=true → zachowaj, ale tylko jeśli wp_post o tym ID
 *     istnieje (else set null — zapobiega sierotom)
 *   - preserveWpMatches=false (default) → zawsze null (fresh state, user
 *     zrobi match ponownie)
 */
export async function importMapFromJson(
  payload: MapExportPayload,
  opts: ImportOptions,
): Promise<ImportResult> {
  validateMapExportPayload(payload);

  const { newName } = opts;
  if (!newName || !newName.trim()) {
    throw new Error("Nowa nazwa mapy jest wymagana");
  }

  const { data: authData, error: authErr } = await supabase.auth.getUser();
  if (authErr) throw authErr;
  if (!authData.user) throw new Error("Brak zalogowanego usera");
  const userId = authData.user.id;

  const websiteId =
    opts.websiteId !== undefined ? opts.websiteId : payload.source_website_id;

  // 1. INSERT thematic_maps — strip id/created_at/updated_at/user_id (nowe wartości)
  const mapRow = { ...payload.map } as Record<string, unknown>;
  delete mapRow.id;
  delete mapRow.created_at;
  delete mapRow.updated_at;
  delete mapRow.user_id;
  mapRow.user_id = userId;
  mapRow.name = newName.trim();
  mapRow.website_id = websiteId;
  mapRow.status = opts.status || "draft";
  mapRow.active_profile_id = null; // remap w kroku 9

  // repair_* transient — czyścimy
  mapRow.repair_status = null;
  mapRow.repair_report = {};
  mapRow.last_repaired_at = null;
  mapRow.repair_rollback_available_until = null;

  // WP matching transient — wyczyść daty (match musi być ponownie uruchomiony jeśli website_id się zmienił)
  if (opts.websiteId !== undefined && opts.websiteId !== payload.source_website_id) {
    mapRow.wp_posts_synced_at = null;
    mapRow.wp_posts_count = 0;
  }

  const { data: newMap, error: mapErr } = await db
    .from("thematic_maps")
    .insert(mapRow)
    .select()
    .single();
  if (mapErr) throw new Error(`INSERT map: ${mapErr.message}`);
  const newMapId = newMap.id as string;

  const seedIdMap = new Map<string, string>();
  const nodeIdMap = new Map<string, string>();
  const entityIdMap = new Map<string, string>();

  // 2. INSERT seeds
  for (const seed of payload.seeds) {
    const row = { ...seed } as Record<string, unknown>;
    const oldId = row.id as string;
    delete row.id;
    delete row.created_at;
    delete row.updated_at;
    row.user_id = userId;
    row.map_id = newMapId;
    // Status reset: ghost/pending detekcja jest specyficzna dla zrodlowej mapy
    // (R7 Repair Engine). Zaimportowana mapa to "swiezy start" — wszystkie seedy
    // jako active, user moze potem odpalic Repair zeby zdemotowac ponownie.
    // Bez tego fixu importy map infekowaly sie statusami i L2 LLM audit
    // filtrowal wszystko (0/N seedów, 0/20 score).
    row.status = "active";

    const { data: newRow, error: err } = await db
      .from("thematic_seeds")
      .insert(row)
      .select("id")
      .single();
    if (err) throw new Error(`INSERT seed "${seed.name}": ${err.message}`);
    seedIdMap.set(oldId, newRow.id);
  }

  // 3. INSERT entities (przed nodes — bo nodes mają primary_entity_id)
  for (const ent of payload.entities) {
    const row = { ...ent } as Record<string, unknown>;
    const oldId = row.id as string;
    delete row.id;
    delete row.created_at;
    delete row.updated_at;
    row.user_id = userId;
    row.map_id = newMapId;

    const { data: newRow, error: err } = await db
      .from("thematic_entities")
      .insert(row)
      .select("id")
      .single();
    if (err) throw new Error(`INSERT entity "${ent.canonical_name}": ${err.message}`);
    entityIdMap.set(oldId, newRow.id);
  }

  // 4. Check existujące WP posts jeśli preserveWpMatches
  let wpPreserved = 0;
  let wpCleared = 0;
  let existingWpPostIds: Set<string> = new Set();
  if (opts.preserveWpMatches) {
    const uniqueWpIds = Array.from(
      new Set(
        payload.nodes
          .map((n) => n.matched_wp_post_id as string | null)
          .filter((id): id is string => !!id),
      ),
    );
    if (uniqueWpIds.length > 0) {
      const { data: foundPosts } = await db
        .from("wp_posts")
        .select("id")
        .in("id", uniqueWpIds);
      existingWpPostIds = new Set(
        (foundPosts || []).map((p: { id: string }) => p.id),
      );
    }
  }

  // 5. INSERT nodes (mapuj seed_id + primary_entity_id + opcjonalnie matched_wp_post_id)
  for (const node of payload.nodes) {
    const row = { ...node } as Record<string, unknown>;
    const oldId = row.id as string;
    const oldSeedId = row.seed_id as string | null;
    const oldEntityId = row.primary_entity_id as string | null;
    const oldMatchedWp = row.matched_wp_post_id as string | null;

    delete row.id;
    delete row.created_at;
    delete row.updated_at;
    row.user_id = userId;
    row.map_id = newMapId;
    row.seed_id = oldSeedId ? seedIdMap.get(oldSeedId) || null : null;
    row.primary_entity_id = oldEntityId
      ? entityIdMap.get(oldEntityId) || null
      : null;

    // matched_wp_post_id handling
    if (opts.preserveWpMatches && oldMatchedWp && existingWpPostIds.has(oldMatchedWp)) {
      row.matched_wp_post_id = oldMatchedWp;
      wpPreserved++;
    } else {
      if (oldMatchedWp) wpCleared++;
      row.matched_wp_post_id = null;
      // Reset match metadata żeby nie zostawić sierot
      row.match_status = "unchecked";
      row.match_confidence = null;
      row.match_reason = null;
      row.match_method = null;
      row.matched_at = null;
      row.refurbish_approved_by_user = null;
      row.refurbish_approved_at = null;
      row.refurbish_approved_by = null;
    }

    const { data: newRow, error: err } = await db
      .from("thematic_nodes")
      .insert(row)
      .select("id")
      .single();
    if (err) throw new Error(`INSERT node "${node.slug}": ${err.message}`);
    nodeIdMap.set(oldId, newRow.id);
  }

  // 6. INSERT entity_attributes (entity_id remap)
  for (const attr of payload.entity_attributes) {
    const row = { ...attr } as Record<string, unknown>;
    delete row.id;
    delete row.created_at;
    delete row.updated_at;
    row.user_id = userId;
    row.map_id = newMapId;
    const oldEntId = attr.entity_id as string;
    const newEntId = entityIdMap.get(oldEntId);
    if (!newEntId) continue; // skip attrs bez mapowania
    row.entity_id = newEntId;

    const { error: err } = await db.from("thematic_entity_attributes").insert(row);
    if (err) throw new Error(`INSERT attribute: ${err.message}`);
  }

  // 7. INSERT relations (from_node_id + to_node_id + map_id remap)
  //    Skip broken refs (nody które nie zostały zaimportowane)
  for (const rel of payload.relations) {
    const row = { ...rel } as Record<string, unknown>;
    delete row.id;
    delete row.created_at;
    delete row.updated_at;
    const oldFrom = rel.from_node_id as string;
    const oldTo = rel.to_node_id as string;
    const newFrom = nodeIdMap.get(oldFrom);
    const newTo = nodeIdMap.get(oldTo);
    if (!newFrom || !newTo) continue; // broken ref, skip
    if (newFrom === newTo) continue; // no_self_relation CHECK
    row.user_id = userId;
    row.map_id = newMapId;
    row.from_node_id = newFrom;
    row.to_node_id = newTo;

    const { error: err } = await db.from("thematic_node_relations").insert(row);
    // uq_relation violations można zignorować — duplikaty już obsługujemy gdzie indziej
    if (err && !err.message.includes("uq_relation")) {
      throw new Error(`INSERT relation: ${err.message}`);
    }
  }

  // 8. INSERT writing_profiles + remap active_profile_id na map
  const profileIdMap = new Map<string, string>();
  for (const profile of payload.writing_profiles) {
    const row = { ...profile } as Record<string, unknown>;
    const oldId = row.id as string;
    delete row.id;
    delete row.created_at;
    delete row.updated_at;
    row.user_id = userId;
    row.map_id = newMapId;

    const { data: newRow, error: err } = await db
      .from("thematic_writing_profiles")
      .insert(row)
      .select("id")
      .single();
    if (err) throw new Error(`INSERT writing profile: ${err.message}`);
    profileIdMap.set(oldId, newRow.id);
  }

  const oldActive = payload.map.active_profile_id as string | null;
  if (oldActive && profileIdMap.has(oldActive)) {
    await db
      .from("thematic_maps")
      .update({ active_profile_id: profileIdMap.get(oldActive) })
      .eq("id", newMapId);
  }

  // 9. Content pipeline (opcjonalne — brak w v1 bez tych pól)
  let ncInserted = 0;
  let kgInserted = 0;
  let serpInserted = 0;

  if (payload.node_content && payload.node_content.length > 0) {
    for (const nc of payload.node_content) {
      const row = { ...nc } as Record<string, unknown>;
      const oldNodeId = row.node_id as string;
      const newNodeId = nodeIdMap.get(oldNodeId);
      if (!newNodeId) continue; // broken ref, skip
      delete row.id;
      delete row.created_at;
      delete row.updated_at;
      row.user_id = userId;
      row.map_id = newMapId;
      row.node_id = newNodeId;
      // Flaga stale — nowa mapa ma ten sam content, ale ma inne seed_id/entity_id
      // → internal links w HTML nadal wskazują na stare slug-i (identyczne w kopii)
      // ale references UUID-owe (primary_entity_id) były używane tylko w context build.
      // Bezpiecznie: oznacz needs_regeneration=false (content identyczny), is_stale=false.
      const { error: err } = await db.from("thematic_node_content").insert(row);
      if (err) {
        // unique constraint (node_id) może strzelić jeśli dziwny stan — pomijamy
        if (!err.message.includes("duplicate")) {
          throw new Error(`INSERT node_content: ${err.message}`);
        }
        continue;
      }
      ncInserted++;
    }
  }

  if (payload.knowledge_graphs && payload.knowledge_graphs.length > 0) {
    for (const kg of payload.knowledge_graphs) {
      const row = { ...kg } as Record<string, unknown>;
      const oldNodeId = row.node_id as string;
      const newNodeId = nodeIdMap.get(oldNodeId);
      if (!newNodeId) continue;
      delete row.id;
      delete row.created_at;
      delete row.updated_at;
      row.user_id = userId;
      row.node_id = newNodeId;

      const { error: err } = await db.from("content_knowledge_graphs").insert(row);
      if (err) {
        if (!err.message.includes("duplicate")) {
          throw new Error(`INSERT knowledge_graph: ${err.message}`);
        }
        continue;
      }
      kgInserted++;
    }
  }

  if (payload.serp_cache && payload.serp_cache.length > 0) {
    for (const sc of payload.serp_cache) {
      const row = { ...sc } as Record<string, unknown>;
      const oldNodeId = row.node_id as string;
      const newNodeId = nodeIdMap.get(oldNodeId);
      if (!newNodeId) continue;
      delete row.id;
      delete row.created_at;
      delete row.updated_at;
      row.node_id = newNodeId;

      const { error: err } = await db.from("content_serp_cache").insert(row);
      if (err) {
        if (!err.message.includes("duplicate")) {
          // non-fatal — SERP cache można regenerować
          continue;
        }
        continue;
      }
      serpInserted++;
    }
  }

  return {
    success: true,
    newMapId,
    newName: newName.trim(),
    counts: {
      seeds: seedIdMap.size,
      nodes: nodeIdMap.size,
      entities: entityIdMap.size,
      entity_attributes: payload.entity_attributes.length,
      relations: payload.relations.length,
      writing_profiles: profileIdMap.size,
      node_content: ncInserted,
      knowledge_graphs: kgInserted,
      serp_cache: serpInserted,
      wp_matches_preserved: wpPreserved,
      wp_matches_cleared: wpCleared,
    },
  };
}
